import type { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type {
  GeneratedDoorPlacement,
  GeneratedDoorPlacementDeprecated,
  DoorDescriptor,
  DoorUnit,
  GeneratedDoorPlacementV2,
} from "building-types/doors/doorTypes";
import { SwingSide } from "building-types/doors/doorTypes";
import type { ExteriorMaterialOverride, InteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { Prop } from "building-types/props/propTypes";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { AllDoorViewNoteTable } from "building-types/settings/viewNotesSettings";
import { mergeWithDefaultsAndOverrides } from "common/utils/mergeWithDefaultsAndOverrides";
import { levelInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import GeomTagGroup from "geom/geomTagGroup";
import { doorDescriptorHasInteriorCasing } from "./doorDescriptorQuery";
import { doorHasExteriorCasing } from "./doorQuery";

export function generatedDoorPlacementDeprecatedNew(
  opts: Partial<GeneratedDoorPlacementDeprecated> &
    Pick<
      GeneratedDoorPlacementDeprecated,
      "descriptor" | "frontRoom" | "halfEdgeIndex" | "param" | "streetOrientation"
    >,
): GeneratedDoorPlacementDeprecated {
  if (!(opts.descriptor !== undefined)) {
    console.assert(false, "Invalid door descriptor");
  }

  const id = uid();

  return mergeWithDefaultsAndOverrides<GeneratedDoorPlacementDeprecated>(
    {
      reverse: false,
      useStripLimits: false,
      param: 36,
      paramNormalized: false,
      paramAnchor: "START",
      verticalOffset: 0,
      lateralOffset: 0,
      awning: undefined,
    },
    opts,
    {
      swingSide:
        opts.swingSide === undefined || (opts.descriptor.requiresDoorSwing && opts.swingSide === SwingSide.NONE)
          ? SwingSide.RIGHT
          : opts.swingSide,
      opening: undefined,
      hasExteriorCasing: doorHasExteriorCasing(opts, opts.exteriorCasing as ExteriorDoorCasingSettings),
      hasInteriorCasing: doorDescriptorHasInteriorCasing(opts.descriptor),
      type: "DOOR",
      id,
    },
  );
}

export function generatedDoorPlacementV2New(
  opts: Partial<GeneratedDoorPlacementV2> &
    Pick<GeneratedDoorPlacementV2, "descriptor" | "frontRoom" | "halfEdgeIndex" | "param" | "streetOrientation">,
): GeneratedDoorPlacementV2 {
  if (!(opts.descriptor !== undefined)) {
    console.assert(false, "Invalid door descriptor");
  }

  const id = uid();

  return mergeWithDefaultsAndOverrides<GeneratedDoorPlacementV2>(
    {
      param: 36,
      paramNormalized: false,
      paramAnchor: "START",
      verticalOffset: 0,
      lateralOffset: 0,
      awning: undefined,
    },
    opts,
    {
      swingSide:
        opts.swingSide === undefined || (opts.descriptor.requiresDoorSwing && opts.swingSide === SwingSide.NONE)
          ? SwingSide.RIGHT
          : opts.swingSide,
      opening: undefined,
      hasExteriorCasing: doorHasExteriorCasing(opts, opts.exteriorCasing as ExteriorDoorCasingSettings),
      hasInteriorCasing: doorDescriptorHasInteriorCasing(opts.descriptor),
      type: "DOOR_V2",
      id,
    },
  );
}

export function doorUnitNew(
  door: GeneratedDoorPlacement,
  streetOrientation: StreetOrientation,
  swingSide: SwingSide,
  prop: Prop | undefined,
  // @TODO(Andrew): use merged settings here
  exteriorCasingSettings: ExteriorDoorCasingSettings | undefined,
  interiorCasingSettings: InteriorDoorCasingSettings | undefined,
  viewNotes: AllDoorViewNoteTable,
): DoorUnit {
  // Reverse the swing side of the door prop by applying a negative scaling on
  // the x axis
  if (prop && swingSide === SwingSide.LEFT) {
    prop = { ...prop, scaleX: -prop.scaleX };
  }

  if (door.handle) {
    BuildingGenerationContext.indexAddNode(door.handle, door.opening?.aabb3 ?? undefined);
  }

  const instanceId = door.frontRoom?.instanceId ?? door.backRoom?.instanceId; // If both rooms are coming from a (different) component instance, we assign the door to the front component instance.

  return {
    ...buildingEntityBaseNew("DOOR", uid(), door.handle),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    doorPlacement: door,
    streetOrientation,
    prop,
    swingSide,
    interiorFront: new GeomTagGroup<InteriorMaterialOverride>(),
    interiorBack: new GeomTagGroup<InteriorMaterialOverride>(),
    exteriorFront: new GeomTagGroup<ExteriorMaterialOverride>(),
    exteriorBack: new GeomTagGroup<ExteriorMaterialOverride>(),
    decorations: new GeomTagGroup<ExteriorMaterialOverride>(),
    awningRoof: new GeomTagGroup<ExteriorMaterialOverride>(),
    exteriorCasingSettings,
    interiorCasingSettings,
    viewNotes,
    headCasingHeight: 0,
    props: [],
  };
}

export function doorDescriptorComputeHeight(
  doorDescriptor: DoorDescriptor,
  doorBaseOffset: number,
  doorSettings: DoorSettings,
): DoorDescriptor {
  const zOffset = doorBaseOffset;

  if (!doorDescriptor.requiresHeightComputation) {
    return {
      ...doorDescriptor,
      zOffset,
      height: levelInchesNew(doorDescriptor.height + zOffset),
      staticHeight: doorDescriptor.height,
    };
  }

  const reportedName = doorDescriptor.reportedName.split("/");
  const height = doorSettings.interiorDoorHeight;

  const heightFt = (height / 12) | 0;
  const heightIn = (height - heightFt * 12) | 0;

  return {
    ...doorDescriptor,
    requiresHeightComputation: false,
    height: levelInchesNew(height + zOffset),
    staticHeight: height,
    zOffset,
    reportedName: reportedName[0] + "/" + heightFt + heightIn,
  };
}
